import { uniqBy, find } from 'lodash';
import moment from 'moment';
import {
  CHART_UTILISATION,
  CHART_SCATTER_PLOT,
  VEHICLE_UTILISATION_GRAPH,
  DRIVER_SAFETY_SUMMARY,
  DRIVER_SAFETY_DETAILED_VIEW,
  CHART_HEAT_MAP,
  CHART_HEAT_MAP_SUMMARY,
  FLEET_COMPOSITION_GRAPH,
  DUE_REPORT_REGEX,
  CHART_SUMMARY_SPLINE,
  CHART_SUMMARY_DISPLAY,
} from './const';

import CustomGraphStyles from './customGraphStyles';

import {
  NitroLoading,
  NitroTable,
  NitroFormatter,
  NitroText,
  NitroIcon,
  NitroAccordion,
} from 'nitro-components';

import InsightTableAccordion from './components/InsightTableAccordion';

import {
  getChartUtilisationCategoryStyle,
  shouldEnableDetails,
  getInsightTableMultipleCategoryHeading,
} from './helpers';

import React, { Component, PropTypes } from 'react';

export const transformChartData = data => {
  const chartType = data.ChartType;
  switch (chartType) {
    case CHART_UTILISATION:
    case DRIVER_SAFETY_SUMMARY:
      return { data: data.Data };
    case CHART_SCATTER_PLOT:
    case DRIVER_SAFETY_DETAILED_VIEW:
      return data;
    case CHART_HEAT_MAP:
    case CHART_HEAT_MAP_SUMMARY:
      return transformHeatMapData(data);
    case CHART_SUMMARY_DISPLAY:
    case CHART_SUMMARY_SPLINE:
      return summaryTransformConfigData(data);
    default:
      return defaultTransformConfigData(data);
  }
};

export const transformTableData = data => {
  const chartType = data.ChartType;
  switch (chartType) {
    case CHART_SCATTER_PLOT:
      return transformVehicleUtilisationTableData(data);

    default:
      return data;
  }
};

export const transformScatterPlot = dataConfig => {
  let series = [];
  const title = dataConfig.Title;
  let tooltip = {};
  let xAxis = {};
  let yAxis = {};
  let limits = {};

  switch (title) {
    case VEHICLE_UTILISATION_GRAPH: {
      const transformedObj = transformVehicleUtilisationGraphSeriesData(
        dataConfig
      );
      series = transformedObj.series;
      limits = transformedObj.limits;
      tooltip = getVehicleUtilisationToolTip(dataConfig.DataType);
      xAxis = getVehicleUtilisationXaxis();
      yAxis = getVehicleUtilisationYaxis(dataConfig.DataType);
      limits.buffer = getBufferValue(dataConfig.DataType);
      break;
    }
    default: {
      series = [];
    }
  }

  const configObj = {
    chart: {
      type: 'scatter',
    },
    title: {
      text: title,
    },

    credits: {
      enabled: false,
    },

    exporting: {
      buttons: {
        contextButton: {
          enabled: false,
        },
      },
    },

    tooltip,
    xAxis,
    yAxis,
    series,
    limits,
  };

  return configObj;
};

export const transformVehicleUtilisationGraphSeriesData = data => {
  // DEFINE BASIC DATA
  const plotData = data.Data;
  const xAxis = data.XAxis[0];
  const yAxis = data.YAxis[0];
  const underUpperThreshold = yAxis.Config.underUpperThreshold;
  const upperLowerThreshold = yAxis.Config.upperLowerThreshold;
  const xaxisTitle = xAxis.Title;
  const yaxisTitle = yAxis.Title;

  // DEFINE THE SERIES PLOT STYLE
  const underSeries = getSeries(
    CustomGraphStyles.acceptable_underutilised_plot_style
  );
  const overSeries = getSeries(
    CustomGraphStyles.acceptable_overutilised_plot_style
  );
  const acceptableSeries = getSeries(CustomGraphStyles.acceptable_plot_style);
  const extremeUnderSeries = getSeries(
    CustomGraphStyles.underutilised_plot_style
  );
  const extremeOverSeries = getSeries(
    CustomGraphStyles.overutilised_plot_style
  );

  let heigestPoint = { YAxis: 0 };
  let lowestPoint = { YAxis: 0 };

  for (const pointData of plotData) {
    const categoryGroup = pointData.CategoryGroup;
    const x = pointData.XAxis;
    const y = pointData.YAxis;
    const identifier = pointData.Identifier;

    if (heigestPoint.YAxis < y) {
      heigestPoint = pointData;
    } else if (lowestPoint.YAxis > y) {
      lowestPoint = pointData;
    }

    const exceededThreshold = pointData.ExceedThreshold;
    switch (categoryGroup) {
      case 'Under utilised':
        if (exceededThreshold) {
          extremeUnderSeries.data.push({ x, y, identifier });
        } else {
          underSeries.data.push({ x, y, identifier });
        }
        break;

      case 'Over utilised':
        if (exceededThreshold) {
          extremeOverSeries.data.push({ x, y, identifier });
        } else {
          overSeries.data.push({ x, y, identifier });
        }
        break;

      case 'Acceptable':
        acceptableSeries.data.push({ x, y, identifier });
        break;
    }
  }

  const overUtilizedLegend = {
    showInLegend: true,
    name: 'Over Utilized',
    data: overSeries.data,
    marker: { symbol: 'circle', radius: 6, fillColor: '#e69898' },
    states: { hover: { enabled: false } },
  };

  const underUtilizedLegend = {
    showInLegend: true,
    name: 'Under Utilized',
    data: underSeries.data,
    marker: {
      symbol: 'circle',
      radius: 6,
      fillColor: '#edda9e',
      borderColor: '#000000',
    },
    states: { hover: { enabled: false } },
  };

  const acceptableLegend = Object.assign(acceptableSeries, {
    showInLegend: true,
    name: 'Acceptable',
    states: { hover: { enabled: false } },
  });

  // holder of the transformed series data
  return {
    series: [
      extremeUnderSeries,
      extremeOverSeries,
      overUtilizedLegend,
      underUtilizedLegend,
      acceptableLegend,
    ],
    limits: {
      heigestPoint,
      lowestPoint,
      underUpperThreshold,
      upperLowerThreshold,
    },
  };
};

export const transformVehicleUtilisationTableData = data => {
  const plotData = data.Data;

  const categoryGroups = new Set();

  const groupByResult = plotData.reduce(function(r, a) {
    r[a.Category] = r[a.Category] || {};
    r[a.Category][a.CategoryGroup] = r[a.Category][a.CategoryGroup] || [];
    r[a.Category][a.CategoryGroup].push(a);
    categoryGroups.add(a.CategoryGroup);
    return r;
  }, Object.create(null));

  let seriesResult = [];
  for (const categorykey in groupByResult) {
    let overUtilized = 0,
      acceptable = 0,
      underUtilized = 0;

    let obj = {
      name: categorykey,
      data: [],
    };

    for (const categoryGroupByKey in groupByResult[categorykey]) {
      if (
        categoryGroupByKey.toString().toLocaleLowerCase() == 'over utilised'
      ) {
        overUtilized = groupByResult[categorykey][categoryGroupByKey].length;
      }
      if (categoryGroupByKey.toString().toLocaleLowerCase() == 'acceptable') {
        acceptable = groupByResult[categorykey][categoryGroupByKey].length;
      }
      if (
        categoryGroupByKey.toString().toLocaleLowerCase() == 'under utilised'
      ) {
        underUtilized = groupByResult[categorykey][categoryGroupByKey].length;
      }
    }

    obj.data.push(
      {
        y: acceptable,
      },
      {
        y: overUtilized,
      },
      {
        y: underUtilized,
      }
    );
    seriesResult.push(obj);
  }

  let transformedObj = Object.assign({}, {});
  transformedObj.series = seriesResult;
  transformedObj.categories = [...categoryGroups].sort(
    (a, b) =>
      a.toLocaleLowerCase().charCodeAt(0) - b.toLocaleLowerCase().charCodeAt(0)
  );
  transformedObj.dataType = data.DataType;
  transformedObj.ChartType = data.ChartType;
  transformedObj.title = data.Title;
  return transformedObj;
};

function renameKeys(obj, newKeys) {
  const keyValues = Object.keys(obj).map(key => {
    const newKey = newKeys[key] || key;
    return { [newKey]: obj[key] };
  });
  return Object.assign({}, ...keyValues);
}

export const transformDashboardData = data => {
  // Incase of due-meter sent the same data
  if (data.DisplayType === 'due-meter' || data.DisplayType === 'odometer')
    return data.Data;

  const graphData = data.Data;
  const seriesData = [];

  const newKeys = { Category: 'name', CategoryValue: 'y' };

  graphData.forEach(item => {
    const renamedObj = renameKeys(item, newKeys);
    seriesData.push(renamedObj);
  });
  return seriesData;
};

export const transformHeatMapData = data => {
  const details = data.Data;

  const newKeys = { Category: 'name', CategoryValue: 'value' };

  const seriesData = details.map(item => {
    const renamedObj = renameKeys(item, newKeys);
    renamedObj.filters = {
      CategoryGroupFilter: item.CategoryGroup,
      categoryFilter: item.Category,
    };

    return renamedObj;
  });

  // Get category from data, as it's all the same
  const category = seriesData.length ? seriesData[0].CategoryGroupLabel : null;

  return {
    series: seriesData,
    Title: data.Title,
    Category: category,
  };
};

export const summaryTransformConfigData = dataConfig => {
  const finObj = {};
  const data = dataConfig.Data;
  const catGrps = uniqBy(data, 'CategoryGroup');
  const cats = uniqBy(data, 'Category').map(ea => {
    const template = {
      data: [],
      config: {
        category: ea.Category,
        displayNumber: ea.DisplayNumber,
        categoryGroups: catGrps.map(x => x.CategoryGroup),
      },
    };
    finObj[ea.Category] = template;
  });

  data.forEach(ea => {
    finObj[ea.Category].data.push(ea.CategoryValue);
  });

  return { summaryData: finObj };
};

export const defaultTransformConfigData = dataConfig => {
  const data = dataConfig.Data;
  let series = [];

  // array of categories - [speeding, parking, tolls]
  const categories = uniqBy(
    data,
    ele =>
      // Group by Category and YAxis as series may be unique by both Category and YAxis props
      `${ele.Category}_${ele.YAxis || ''}`
  );

  // array of categoty groups
  let categoryGroups = uniqBy(data, 'CategoryGroup');

  // array of categoty group values - [jun, jul, aug]
  categoryGroups = categoryGroups.map(item => item.CategoryGroup);
  const yAxisGroups = uniqBy(data, 'YAxis');
  series = categories.map(category => {
    // get the current category in the map - eg speeding
    const currentCategory = category.Category;
    const currentYAxis = !category.YAxis ? null : category.YAxis;
    // console.log(currentCategory);

    // filter original data to return entries that match current category, eg speeding
    const dataFilteredByCurrentCategory = data.filter(
      item => item.Category === currentCategory && item.YAxis === category.YAxis
    );

    // console.log(dataFilteredByCurrentCategory);

    // array of values to use with highCharts eg [1,3,0,4,5]
    const values = categoryGroups.map(categoryGroup => {
      // check dataFilteredByCurrentCategory for an entry where the current categoryGroup matches the categoryGroup
      const valueForThisCategory = dataFilteredByCurrentCategory.find(
        item => item.CategoryGroup === categoryGroup
      );

      let value;

      // if categoryGroups match, return that categoryGroup's value
      if (valueForThisCategory) {
        value = valueForThisCategory.CategoryValue;
      } else {
        // if categoryGroups DO NOT match, return 0
        value = 0;
      }
      const filters = {};

      if (valueForThisCategory && valueForThisCategory.year) {
        const dtStart = moment({
          year: valueForThisCategory.year,
          month: parseInt(`${valueForThisCategory.month}`, 10) - 1,
        });

        filters.StartDate = dtStart.format('YYYY-MM-DD');
        filters.EndDate = dtStart.endOf('month').format('YYYY-MM-DD');
      } else if (valueForThisCategory && valueForThisCategory.Category) {
        filters.categoryFilter = valueForThisCategory.Category;
      }

      if (valueForThisCategory && valueForThisCategory.CategoryGroupFilter) {
        filters.categoryGroupFilter = valueForThisCategory.CategoryGroupFilter;
      }

      if (valueForThisCategory && valueForThisCategory.CategoryFilter) {
        filters.categoryFilter = valueForThisCategory.CategoryFilter;
      }

      // return value;
      // return {y:value,year:valueForThisCategory.year,month:valueForThisCategory.month,MyCategory:valueForThisCategory.Category};
      return {
        y: value,
        filters,
        name: currentCategory,
      };
    });

    // create new category object with category name and data values
    const seriesObject = {
      name: currentCategory,
      data: values,
    };

    const yAxis = find(
      dataConfig.YAxis || [],
      yAxis => yAxis.Name === currentYAxis
    );

    if (yAxis) {
      if (yAxis.ChartType) {
        // If yAxis configuration has ChartType specified set the type of all the series objects that belong to this yAxis.
        seriesObject.type = yAxis.ChartType;
      }

      // Merge SeriesConfig from api to the series.
      Object.assign(seriesObject, yAxis.SeriesConfig || {});

      if (category.YAxis) {
        // Only set YAxis prop of series if the property has been specified in data itself.
        seriesObject.yAxis = category.YAxis;
      }
    }

    // push new category object to the series array for use with high charts
    series.push(seriesObject);

    return seriesObject;
  });
  // config data

  const formattedData = {
    dataType: dataConfig.DataType,
    title: dataConfig.Title,
    xAxis: {
      categories: categoryGroups,
    },
    series,
  };

  let yAxisObjects = [];

  if (dataConfig.YAxis && dataConfig.YAxis.length) {
    dataConfig.YAxis.forEach(yAxis => {
      let yAxisObject = {
        id: yAxis.Name,
        title: { text: yAxis.Title },
        allowDecimals: false,
      };

      yAxisObject = Object.assign({}, yAxisObject, yAxis.Config || {});

      yAxisObjects.push(yAxisObject);
    });
  }

  if (yAxisObjects.length) {
    // set yAxis prop only if there are yAxis data in configData.
    formattedData.yAxis = yAxisObjects;
  }

  return formattedData;
};

export const transformTableDataToInsightTableComponent = (
  tableData,
  clickCategoryCb
) => {
  let data = tableData;
  let categories = [];
  const titleType = data.Title;

  // Use alternate render method for Vehicle Composoition Table
  if (titleType === FLEET_COMPOSITION_GRAPH) {
    return getInsightVehicleCompositionTable(data);
  }

  // Place holder for on row click for other reports
  let categoryCallBack = () => {};
  switch (titleType) {
    case VEHICLE_UTILISATION_GRAPH: {
      data = transformVehicleUtilisationTableData(tableData);
      categories = data.categories;
      categoryCallBack = clickCategoryCb;
      break;
    }
    default: {
      data = tableData;
      categories = data.xAxis.categories;
      break;
    }
  }

  const alignment = getInsightTableAlignment(data.dataType);

  const isSingleCategory = categories.length === 1 && !categories[0];

  const endingColumns = getInsightTableEndingColumns(data.title);
  // extra space for columns
  if (shouldEnableDetails(data.ChartType)) endingColumns.push({ name: ' ' });

  const headers = getInsightTableHeaders(data, endingColumns, isSingleCategory);

  const tableBody = isSingleCategory
    ? getInsightsTableBodyForSingleCategories(data)
    : getInsightTableBodyForMultipleCategories(
        categories,
        data,
        alignment,
        categoryCallBack
      );
  return getInsightTable(headers, tableBody, alignment);
};

const getInsightTableAlignment = dataType => {
  switch (dataType) {
    case 'Currency':
      return 'right';

    case 'Percentage':
      return 'center';

    default:
      return 'center';
  }
};

const getInsightTableEndingColumns = title => {
  // NITRO-1207 rename columns to COUNT for "DUE" report type
  DUE_REPORT_REGEX.lastIndex = 0;
  if (DUE_REPORT_REGEX.test(title)) {
    return [{ name: 'Count' }];
  }

  return [{ name: 'Total' }];
};

const getInsightTableHeaders = (data, endingColumns, isSingleCategory) => {
  if (isSingleCategory) return [{ name: 'Category' }, ...endingColumns];

  let header = [
    { name: getInsightTableMultipleCategoryHeading(data.title) },
    ...endingColumns,
  ];

  // NITRO-1207 removing series header for "DUE" report type
  // from table view
  DUE_REPORT_REGEX.lastIndex = 0;
  if (!DUE_REPORT_REGEX.test(data.title) || data.series.length > 1) {
    header.splice(1, 0, ...data.series);
  }

  return header;
};

const getInsightsTableBodyForSingleCategories = data =>
  data.series.map((series, index) => (
    <tr key={index} className="table-body-row">
      <td className="table-data table-data-no-pointer">
        {series.data[0].name}
      </td>
      <td className="table-data table-data-no-pointer">
        <NitroFormatter data={series.data[0].y} type={data.dataType} />
      </td>
    </tr>
  ));

const getInsightTableBodyForMultipleCategories = (
  categories,
  data,
  alignment,
  clickCategoryCb
) =>
  categories.map((category, index) => {
    const tableRowData = data.series.map((series, sindex) => {
      const value = series.data[index] ? series.data[index].y : 0;
      return (
        <td
          key={sindex}
          className={`table-data table-data-no-pointer table-data-${alignment}`}
        >
          <NitroFormatter data={value} type={data.dataType} />
        </td>
      );
    });

    // NITRO-1047 - add short term client side fix to exclude
    // average column from total calculation for 'Fleet Cost Chart'
    // In a long term solution, we should move total calculation logic
    // to server side

    let totalNumber;

    // add special rule for fleet cost chart
    if (data.title === 'Fleet Cost Chart') {
      totalNumber = data.series.reduce((acc, cur) => {
        const value =
          // ensure we don't add Average field to total calculation
          cur.data[index] && cur.name !== 'Average' ? cur.data[index].y : 0;
        acc += value;
        return acc;
      }, 0);
    } else {
      // normal total calculation rule
      totalNumber = data.series.reduce((acc, cur) => {
        const value = cur.data[index] ? cur.data[index].y : 0;
        acc += value;
        return acc;
      }, 0);
    }

    const variant = getChartUtilisationCategoryStyle(category);

    let showRowData = true;
    DUE_REPORT_REGEX.lastIndex = 0;
    if (DUE_REPORT_REGEX.test(data.title) && data.series.length === 1) {
      showRowData = false;
    }

    // NITRO-1324 - title case category label fix
    const toTitleCase = str =>
      str.replace(
        /\w\S*/g,
        txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
      );
    const catLabel = category.replace ? toTitleCase(category) : category;

    //TODO Need to refactor this with proper flags from the BE - sriram.
    return (
      <tr
        key={index}
        className="table-body-row"
        onClick={() => clickCategoryCb({ categoryGroupFilter: category })}
      >
        <td className="table-data table-data-no-pointer">
          <NitroText bold variant={variant}>
            {catLabel}
          </NitroText>
        </td>
        {showRowData && tableRowData}
        <td
          className={`table-data table-data-no-pointer table-data-${alignment}`}
        >
          <NitroFormatter
            data={totalNumber}
            type={data.dataType}
            bold
            variant={variant}
          />
        </td>
        {shouldEnableDetails(data.ChartType) && (
          <td>
            <NitroText variant={variant}>Details</NitroText>
            <NitroIcon
              name="arrow-right"
              size={8}
              left={12}
              variant={variant}
            />
          </td>
        )}
      </tr>
    );
  });

const getInsightTable = (headers, tableBody, alignment) => (
  <NitroTable highlighted>
    <thead>
      <tr>
        {headers.map((series, index) => (
          <th key={index} className={`table-header-${alignment}`}>
            {series.name}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>{tableBody}</tbody>
  </NitroTable>
);

const getInsightVehicleCompositionTable = data => {
  const tableData = data.series;

  const { CurrentMonth, PreviousMonth } = tableData[0];
  const headers = [
    { name: data.Category },
    { name: CurrentMonth },
    { name: PreviousMonth },
  ];

  const rows = tableData.map(row => ({
    header: [
      row.name,
      row.CurrentLeases,
      <div>
        {getTableTrendIcon(row.PreviousMovement)}
        {row.PreviousMovement}
      </div>,
    ],
    categorySubName: row.Children[0].CategorySubName,
    children: row.Children.map(child => [
      child.CategorySub,
      child.CurrentLeases,
      <div>
        {getTableTrendIcon(child.PreviousMovement)}
        {child.PreviousMovement}
      </div>,
    ]),
  }));

  return (
    <div>
      <NitroTable highlighted>
        <thead>
          <tr>
            {headers.map((series, index) => (
              <th key={index} className="table-header-center">
                {series.name}
              </th>
            ))}
          </tr>
        </thead>
        {rows.map((row, index) => (
          <InsightTableAccordion
            header={row.header}
            content={row.children}
            categorySubName={row.categorySubName}
            key={`table-accrodion-${index}`}
          />
        ))}
      </NitroTable>
    </div>
  );
};

const getSeries = style => ({
  showInLegend: false,
  marker: {
    fillColor: style.fillColor || '#fff',
    radius: 6,
    lineColor: style.lineColor,
    lineWidth: 1,
    symbol: style.symbol,
    states: {
      hover: {
        enabled: false,
      },
    },
  },
  data: [],
});

const getBufferValue = type => (type === 'Kilometres' ? 1000 : 20);

const getTableTrendIcon = value => {
  let icon = 'trend-mobile-equal';

  if (value > 0) {
    icon = 'trend-mobile-up';
  } else if (value < 0) {
    icon = 'trend-mobile-down';
  }

  return <NitroIcon name={icon} size={10} right={10} variant="base3" />;
};

const getVehicleUtilisationToolTip = type => {
  const tip =
    type === 'Kilometres'
      ? {
          formatter() {
            return (
              `${' <br/>' + 'Vehicle Regno: <b>'}${
                this.point.identifier
              }<b><br/>` +
              `Elapsed Lease: <b>${this.x}%</b><br>kilometer variance: <b>${this.y}k</b>` +
              '<br/> '
            );
          },
        }
      : {
          formatter() {
            return (
              `${' <br/>' + 'Vehicle Regno: <b>'}${
                this.point.identifier
              }<b><br/>` +
              `Elapsed Lease: <b>${this.x}%</b><br>Vehicle Utilisation: <b>${this.y}%</b>` +
              '<br/> '
            );
          },
        };

  return tip;
};

const getVehicleUtilisationXaxis = () => ({
  labels: {
    formatter() {
      return `${this.value}%`;
    },
  },
  floor: 0,
  title: {
    text: 'Elapsed Time',
  },
});

const getVehicleUtilisationYaxis = type => {
  const axis =
    type === 'Kilometres'
      ? {
          labels: {
            formatter() {
              return `${this.value}k`;
            },
          },
          title: {
            text: 'Kilometer variance',
          },
        }
      : {
          labels: {
            formatter() {
              return `${this.value}%`;
            },
          },
          title: {
            text: 'Vehicle Utlisation',
          },
        };
  return axis;
};

export const DriverSafetyDataTransformation = data => {};



// WEBPACK FOOTER //
// ./src/features/insights/DataTransformer.js